import { serverNowMs } from './clock';

/**
 * Round boundaries (the DPIN 5-minute market) fall on the 5-minute wall-clock grid. At each one every open
 * client refreshes in the same second: round follow, SSE settlement events, asset reads. This module spreads
 * that burst. It changes nothing outside the DPIN build (mode dpin-bsc) and nothing in the middle of a round.
 */

/** Per-client jitter is drawn once per page load from [0, REFRESH_JITTER_MAX_MS). */
export const REFRESH_JITTER_MAX_MS = 2_000;
/** Triggers of one key that fire within this gap of each other make one refresh. */
export const REFRESH_DEDUPE_MS = 500;
/** The wall-clock grid of the round boundaries, in seconds. */
const BOUNDARY_PERIOD_S = 300;
/**
 * The window opens this long before a boundary. It covers the error of the server clock estimate (a Date header
 * is a whole second). It stays short: a refresh in the last trading seconds of a round should not wait.
 */
const BOUNDARY_LEAD_S = 2;
/** The window ends this long after a boundary. The measured burst sits in this first minute. */
const BOUNDARY_WINDOW_S = 60;

const JITTER_MODE = 'dpin-bsc';

/** One draw, clamped into [0, REFRESH_JITTER_MAX_MS): a value that is not finite gives 0, so a bad draw never stalls a flip. */
export const jitterFromRandom = (random: () => number) => {
  const ms = Math.floor(random() * REFRESH_JITTER_MAX_MS);
  return Number.isFinite(ms) ? Math.min(Math.max(ms, 0), REFRESH_JITTER_MAX_MS - 1) : 0;
};

// Fixed for the page load: one client always waits the same time, so the clients keep their spread.
let clientJitterMs = jitterFromRandom(Math.random);

export const refreshJitterMs = () => clientJitterMs;

/** Test-only: pin the jitter, or draw a new one when called without a value. */
export const __setClientJitterMs = (ms?: number) => {
  clientJitterMs = ms ?? jitterFromRandom(Math.random);
};

/** True in the first minute after a 5-minute mark, and in the last 2 s before one. `nowMs`: server time. */
export const inBoundaryWindow = (nowMs = serverNowMs()) => {
  const second = (nowMs / 1000) % BOUNDARY_PERIOD_S;
  return second < BOUNDARY_WINDOW_S || second >= BOUNDARY_PERIOD_S - BOUNDARY_LEAD_S;
};

/**
 * The delay a boundary-aligned refresh adds: the client jitter in the DPIN build inside the boundary window,
 * else 0. A first load, a reader's own action and a refresh after the reader's own trade do not call this.
 */
export const boundaryJitterMs = (mode: string = import.meta.env.MODE) =>
  mode === JITTER_MODE && inBoundaryWindow() ? clientJitterMs : 0;

// One slot per key: the pending timer, the run it will call (the latest trigger's) and the time of the last run.
// A slot lives while a run waits and for REFRESH_DEDUPE_MS after a run. After that gap it holds nothing the next
// trigger needs, so it is deleted: the map stays as small as the number of keys that refreshed in the last 500 ms.
type Slot = { timer?: ReturnType<typeof setTimeout>; run?: () => void; ranAt: number };
const slots = new Map<string, Slot>();

/** Test-only: the number of keys that hold a slot. */
export const __boundaryRefreshSlotCount = () => slots.size;

/** Test-only: drop every pending run and the run times (module state outlives a test). */
export const __resetBoundaryRefresh = () => {
  for (const slot of slots.values()) clearTimeout(slot.timer);
  slots.clear();
};

/**
 * Runs `run` for a trigger that comes from a server push or a timer aligned to the wall clock.
 * - Outside the DPIN build `run` runs at once and nothing is kept.
 * - A trigger that finds a pending run for the key joins it, and its `run` replaces the pending one. The latest
 *   caller is the one most likely to be alive (an earlier caller may have unmounted).
 * - A trigger within REFRESH_DEDUPE_MS after a run waits at least the rest of that gap, so no trigger is lost.
 * - Inside the boundary window the wait is the longer of that gap and the client jitter.
 */
export const scheduleBoundaryRefresh = (key: string, run: () => void, mode: string = import.meta.env.MODE) => {
  if (mode !== JITTER_MODE) return run();
  const slot = slots.get(key) ?? { ranAt: Number.NEGATIVE_INFINITY };
  slots.set(key, slot);
  slot.run = run;
  if (slot.timer !== undefined) return;
  const fire = () => {
    const ranAt = Date.now();
    slot.timer = undefined;
    slot.ranAt = ranAt;
    // Delete the slot after the gap, unless a newer run or a waiting run has taken it over (it then deletes it).
    // Set before the run: a run that throws leaves no slot behind.
    setTimeout(() => {
      if (slot.timer === undefined && slot.ranAt === ranAt && slots.get(key) === slot) slots.delete(key);
    }, REFRESH_DEDUPE_MS);
    slot.run?.();
  };
  // Date.now() is not monotonic (NTP step, wake from sleep). A run time in the future, or older than the gap,
  // is no recent run: a step must not suppress the next refresh for the size of the step.
  const sinceRun = Date.now() - slot.ranAt;
  const gap = sinceRun >= 0 && sinceRun < REFRESH_DEDUPE_MS ? REFRESH_DEDUPE_MS - sinceRun : 0;
  const wait = Math.max(boundaryJitterMs(mode), gap);
  if (wait <= 0) fire();
  else slot.timer = setTimeout(fire, wait);
};
